import { Loader2, RefreshCw, Sparkles, X } from 'lucide-react'
import { Design_ } from 'src/api/db/apiDesigns'
import { buildManualMockupPayload } from 'src/api/server/buildGenerateMockupsPayload'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import { EmptyState } from 'src/shared/components/EmptyState'
import { useGenerateMockups } from 'src/api/server/useGenerateMockups'
import { MockupLoadingView } from '../MockupLoadingView'

export const PreviewTab = ({
  design_,
  quote_sub_item_id,
  quote_item_id,
  product_name,
  mockup_urls,
  decoration_name,
  onDeleteMockup,
}: {
  design_: Partial<Design_>
  quote_sub_item_id?: number
  quote_item_id?: number
  product_name?: string
  mockup_urls?: string[] | null
  decoration_name?: string | null
  onDeleteMockup?: (url: string) => void
}) => {
  const { t } = useLocalization()
  const generateMockups = useGenerateMockups()

  const canGenerateMockups = Boolean(
    quote_sub_item_id &&
      quote_item_id &&
      design_?.image_url &&
      design_?.design_url
  )

  const handleGenerateMockups = () => {
    const { payload } = buildManualMockupPayload({
      quote_sub_item_id: quote_sub_item_id as number,
      quote_item_id: quote_item_id as number,
      product_name: product_name ?? t('Product', 'Produkt'),
      decoration_name: decoration_name ?? null,
      design: design_,
    })
    if (!payload) return
    generateMockups.mutate(payload)
  }

  const displayMockupUrls =
    mockup_urls ?? generateMockups.data?.mockup_urls ?? []
  const hasMockups = displayMockupUrls.length > 0

  return (
    <Column className="gap-4 w-[600px] min-h-[400px]">
      {generateMockups.isPending ? (
        <MockupLoadingView
          loadingText={t('Generating mockups...', 'Genererar mockups...')}
        />
      ) : !hasMockups ? (
        <EmptyState
          withCard={false}
          icon={<Sparkles className="w-9 h-9" />}
          iconColor="blue"
          title={t('Generate product mockups', 'Generera produktmockups')}
          description={
            canGenerateMockups
              ? t(
                  'Create realistic mockups showing your design on the product. Takes about 15 seconds.',
                  'Skapa realistiska mockups som visar din design på produkten. Tar cirka 15 sekunder.'
                )
              : t(
                  'Select a product image and logo to generate mockups.',
                  'Välj en produktbild och logotyp för att generera mockups.'
                )
          }
          image={
            canGenerateMockups && design_?.image_url
              ? design_.image_url
              : undefined
          }
          button={
            canGenerateMockups ? (
              <Button variant="primary" onClick={handleGenerateMockups}>
                <Sparkles className="w-4 h-4 mr-2" />
                {t(
                  'Generate mockups (~15 sec)',
                  'Generera mockups (~15 sek)'
                )}
              </Button>
            ) : undefined
          }
          className="flex-1 py-12 bg-gray-100 rounded-lg border border-gray-200/80"
        />
      ) : (
        <Column className="gap-4 min-h-0">
          <Row className="justify-end shrink-0">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleGenerateMockups}
              disabled={
                generateMockups.isPending ||
                !quote_sub_item_id ||
                !quote_item_id ||
                !design_?.image_url ||
                !design_?.design_url
              }
            >
              {generateMockups.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              {t('Re-generate images', 'Generera om bilder')}
            </Button>
          </Row>
          <div className="flex flex-col gap-3 w-full min-h-0 flex-1 max-h-[500px] overflow-y-auto overscroll-contain">
            {displayMockupUrls.map((url, index) => (
              <div
                key={url}
                className="relative w-full shrink-0 rounded-lg border-2 overflow-hidden group"
              >
                <img
                  src={url}
                  alt={t(`Mockup ${index + 1}`, `Mockup ${index + 1}`)}
                  className="w-full h-full object-contain bg-white"
                />
                {onDeleteMockup && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      onDeleteMockup(url)
                    }}
                    className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/60 hover:bg-black/80 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label={t('Delete mockup', 'Ta bort mockup')}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </Column>
      )}
    </Column>
  )
}
